import { isEmpty, orderBy } from 'lodash'

import { getIsProductReadyForSale } from 'modules/ProductInfo/hooks/useReadyForSaleTasks'
import {
  CHECKOUT_BASE_URL,
  buildTaggedLink,
  getOneTimeProductPagePath,
  getStorefrontPagePath,
} from 'modules/ProductInfo/utils/shareLinks'

export { CHECKOUT_BASE_URL, buildTaggedLink }

export const PURCHASE_REPORT_FUNCTION = 'getPurchaseReport'

// Every coach with an org sees the Attribution page, and Trybe admins see it from any account. The report
// itself is checked again on the server, which only answers an org's own admins.
export function canSeeReports({ coachOrgId, trybeAdminValue }) {
  return Boolean(coachOrgId) || trybeAdminValue === 'admin'
}

export const CUSTOM_PRESET_ID = 'custom'

// One button per platform, with no utm_content: the campaign name already says which post or
// email a link is for. Source and medium match the Share menu's links (LINK_PLACEMENTS), so both
// group together in reports.
const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', utm_source: 'ig', utm_medium: 'social' },
  { id: 'youtube', label: 'YouTube', utm_source: 'youtube', utm_medium: 'social' },
  { id: 'tiktok', label: 'TikTok', utm_source: 'tiktok', utm_medium: 'social' },
  { id: 'facebook', label: 'Facebook', utm_source: 'facebook', utm_medium: 'social' },
  { id: 'email', label: 'Email', utm_source: 'email', utm_medium: 'email' },
]

export const LINK_PRESETS = [...PLATFORMS, { id: CUSTOM_PRESET_ID, label: 'Custom' }]

export const TRACKED_LINK_LIMITS = { name: 80, description: 200, tag: 40 }

// "Black Friday reel" becomes "black-friday-reel". The slug is the link's
// utm_campaign, so it doubles as the link's id and has to be unique per coach.
export function slugifyCampaign(name) {
  return String(name ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, TRACKED_LINK_LIMITS.tag)
    .replace(/-+$/g, '')
}

export function isCampaignTaken(slug, trackedLinks) {
  if (!slug || !trackedLinks || typeof trackedLinks !== 'object') return false
  return Object.entries(trackedLinks).some(
    ([linkId, link]) => linkId === slug || (link && typeof link === 'object' && link.utm_campaign === slug)
  )
}

export const SUBSCRIPTION_PRODUCT_KEY = 'subscription'
export const SUBSCRIPTION_PRODUCT_LABEL = 'App subscription'

export function getMembershipName(subscriptionName) {
  const name = String(subscriptionName ?? '').trim()
  if (!name) return 'Membership'
  return /membership|subscription/i.test(name) ? name : `${name} membership`
}

// The products a coach can link to, membership first, then one-time products
// newest first like the Sell page. Both go through the dashboard's ready for sale
// rule. The subscription has no ready flag, so the rule falls back to its published
// checkout page, and it also needs plans, since the storefront checkout will not
// load without them.
export function getSellableProducts({
  subscriptionName,
  subscriptionPlans,
  subscriptionCheckoutPage,
  subscriptionSalesPage,
  oneTimeProducts,
}) {
  const products = []
  if (
    !isEmpty(subscriptionPlans) &&
    getIsProductReadyForSale({
      product: null,
      publishedCheckoutPage: isEmpty(subscriptionCheckoutPage) ? null : subscriptionCheckoutPage,
    })
  ) {
    // Named like the Sell page's card, since the Stripe product name often isn't recognizable
    products.push({
      key: SUBSCRIPTION_PRODUCT_KEY,
      name: SUBSCRIPTION_PRODUCT_LABEL,
      path: getStorefrontPagePath({ basePath: '', salesPage: subscriptionSalesPage }),
    })
  }
  const readyOneTimeProducts = Object.entries(oneTimeProducts || {}).filter(
    ([, product]) =>
      product &&
      typeof product === 'object' &&
      product.slug &&
      getIsProductReadyForSale({ product, publishedCheckoutPage: product.siteCheckout || null })
  )
  orderBy(readyOneTimeProducts, ([, product]) => product.createdAt ?? 0, 'desc').forEach(([id, product]) => {
    products.push({
      key: id,
      name: String(product.stripeProductName || '').trim() || 'Untitled product',
      path: getOneTimeProductPagePath({ slug: product.slug, salesPage: product.salesPage }),
    })
  })
  return products
}

// Every subscription link reads as the App subscription, whatever name it was saved with. Links
// saved before links had a product all went to the subscription's page.
export function getTrackedLinkPageName(link) {
  if (!link?.productKey || link.productKey === SUBSCRIPTION_PRODUCT_KEY) {
    return SUBSCRIPTION_PRODUCT_LABEL
  }
  return link.productName || 'Untitled product'
}

// The platform a saved link was made for, read back from its source and medium. Links saved
// before the builder dropped utm_content still match by source and medium. Custom links show
// their source.
export function getTrackedLinkChannelLabel(link) {
  const preset = PLATFORMS.find((p) => p.utm_source === link?.utm_source && p.utm_medium === link?.utm_medium)
  if (preset) return preset.label
  return link?.utm_source || 'Custom'
}

const cleanTag = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()

const PREVIEW_CAMPAIGN = 'black-friday-reel'

export const UTM_OVERRIDE_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content']

// What the coach types into a tag field: lowercase, spaces become dashes, and anything
// outside letters, numbers, dashes and underscores is dropped.
export function sanitizeUtmValue(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_-]/g, '')
}

// The tags a link gets from its placement and name alone. Custom has no source or medium
// of its own, so the coach fills those in.
export function getDefaultLinkTags({ presetId, name, customSource, customMedium }) {
  const preset = LINK_PRESETS.find((p) => p.id === presetId)
  const isCustom = presetId === CUSTOM_PRESET_ID
  return {
    utm_source: isCustom ? cleanTag(customSource) : preset?.utm_source || '',
    utm_medium: isCustom ? cleanTag(customMedium) : preset?.utm_medium || '',
    utm_campaign: slugifyCampaign(name),
    utm_content: '',
  }
}

// Default tags with the coach's edits on top. An empty edit falls back to the default.
export function getLinkTags({ utmOverrides = {}, ...defaultArgs }) {
  const tags = getDefaultLinkTags(defaultArgs)
  UTM_OVERRIDE_KEYS.forEach((key) => {
    const override = sanitizeUtmValue(utmOverrides[key])
    if (override) tags[key] = override
  })
  return tags
}

// The link the form would save, shown live while the coach fills it in. With no name and
// no campaign edit it shows a sample campaign so the coach can see where the name goes.
export function buildTrackedLinkPreview({ coachSlug, product, ...tagArgs }) {
  const tags = getLinkTags(tagArgs)
  return buildTaggedLink(
    coachSlug,
    { ...tags, utm_campaign: tags.utm_campaign || PREVIEW_CAMPAIGN },
    product?.path || ''
  )
}

// Returns { link } ready to save, or { error } with a message for the coach. The record keeps
// the exact tags in its URL, since reports match purchases on every tag of a saved link.
export function buildTrackedLinkRecord({
  coachSlug,
  product,
  presetId,
  name,
  description,
  customSource,
  customMedium,
  utmOverrides = {},
  trackedLinks,
  now = Date.now(),
}) {
  if (!product?.key) return { error: 'Pick which page the link is for.' }
  const preset = LINK_PRESETS.find((p) => p.id === presetId)
  if (!preset) return { error: 'Pick where the link will go.' }
  const trimmedName = String(name ?? '').trim()
  if (!trimmedName) return { error: 'Give the link a name.', field: 'name' }
  if (trimmedName.length > TRACKED_LINK_LIMITS.name) {
    return { error: `Keep the name to ${TRACKED_LINK_LIMITS.name} characters or fewer.`, field: 'name' }
  }
  const trimmedDescription = String(description ?? '').trim()
  if (trimmedDescription.length > TRACKED_LINK_LIMITS.description) {
    return {
      error: `Keep the description to ${TRACKED_LINK_LIMITS.description} characters or fewer.`,
      field: 'description',
    }
  }

  const { utm_source, utm_medium, utm_campaign, utm_content } = getLinkTags({
    presetId,
    name: trimmedName,
    customSource,
    customMedium,
    utmOverrides,
  })
  const campaignEdited = Boolean(sanitizeUtmValue(utmOverrides.utm_campaign))
  if (!utm_campaign) return { error: 'Use at least one letter or number in the name.', field: 'name' }
  if (utm_campaign.length > TRACKED_LINK_LIMITS.tag) {
    return {
      error: `Keep utm_campaign to ${TRACKED_LINK_LIMITS.tag} characters or fewer.`,
      field: campaignEdited ? 'utm_campaign' : 'name',
    }
  }
  if (isCampaignTaken(utm_campaign, trackedLinks)) {
    return {
      error: `You already have a link called "${utm_campaign}". Pick a different ${
        campaignEdited ? 'utm_campaign' : 'name'
      }.`,
      field: campaignEdited ? 'utm_campaign' : 'name',
    }
  }
  if (!utm_source || !utm_medium) {
    return { error: 'Fill in both source and medium.', field: utm_source ? 'utm_medium' : 'utm_source' }
  }
  if (utm_source.length > TRACKED_LINK_LIMITS.tag || utm_medium.length > TRACKED_LINK_LIMITS.tag) {
    return {
      error: `Keep source and medium to ${TRACKED_LINK_LIMITS.tag} characters or fewer.`,
      field: utm_source.length > TRACKED_LINK_LIMITS.tag ? 'utm_source' : 'utm_medium',
    }
  }

  const tags = { utm_source, utm_medium, utm_campaign }
  if (utm_content) tags.utm_content = utm_content
  const link = {
    name: trimmedName,
    ...tags,
    url: buildTaggedLink(coachSlug, tags, product.path),
    productKey: product.key,
    productName: product.name,
    createdAt: now,
  }
  if (trimmedDescription) link.description = trimmedDescription
  return { link }
}

const LEADING_COLUMNS = [
  'date',
  'customerEmail',
  'source',
  'paymentMethod',
  'channel',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'referrer',
  'landingPage',
  'attribution_origin',
  'webQuiz',
  'appQuiz',
]

const TRAILING_COLUMNS = [
  'planName',
  'purchaseType',
  'productName',
  'price',
  'currency',
  'startedAsTrial',
  'status',
  'convertedAt',
  'paymentsCount',
  'revenueToDate',
  'canceledAt',
]

const pad = (n) => String(n).padStart(2, '0')

// YYYY-MM-DD in the browser's time zone.
export function formatLocalDate(ms) {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return ''
  const d = new Date(ms)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function formatCents(cents) {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return ''
  return (cents / 100).toFixed(2)
}

// UTM values and referrers come from public links, so a value starting with a
// formula character could run as a formula when the coach opens the file in a
// spreadsheet. A leading apostrophe makes the spreadsheet treat it as text.
function neutralizeFormula(value) {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value
}

export function escapeCsvField(value) {
  if (value === undefined || value === null) return ''
  const text = String(value)
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

function cellValue(row, column) {
  switch (column) {
    case 'date':
      return formatLocalDate(row.createdAt)
    case 'convertedAt':
    case 'canceledAt':
      return formatLocalDate(row[column])
    case 'price':
    case 'pricePaid':
    case 'revenueToDate':
      return formatCents(row[column])
    // The stored field is isTrial, but it records how the purchase started, not whether it is in a
    // trial now, so the download names it startedAsTrial
    case 'startedAsTrial':
      return typeof row.isTrial === 'boolean' ? String(row.isTrial) : ''
    case 'channel':
      return isDirectRow(row) ? DIRECT_CHANNEL_LABEL : neutralizeFormula(row.channel)
    default: {
      const value = row[column]
      if (column.endsWith('At') && typeof value === 'number') return formatLocalDate(value)
      if (value === undefined || value === null) return ''
      return typeof value === 'string' ? neutralizeFormula(value) : String(value)
    }
  }
}

// Stored fields already shown under another column: createdAt as date, isTrial as startedAsTrial
const SHOWN_AS_OTHER_COLUMNS = ['createdAt', 'isTrial']

// The fixed columns, plus a column for every other field any row has (experiments first, then the
// rest by name), so a coach gets whatever data exists and no empty columns for data they don't have.
// The server leaves out fields the coach must not see.
export function getPurchaseReportColumns(rows = []) {
  const known = new Set([...LEADING_COLUMNS, ...TRAILING_COLUMNS, ...SHOWN_AS_OTHER_COLUMNS])
  const extraKeys = new Set()
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!known.has(key)) extraKeys.add(key)
    })
  })
  const isExperiment = (key) => key.startsWith('exp_')
  const extras = [...extraKeys].sort((a, b) => isExperiment(b) - isExperiment(a) || a.localeCompare(b))
  return [...LEADING_COLUMNS, ...extras, ...TRAILING_COLUMNS]
}

// Purchases with no known source. The server stores them as channel 'none'.
export const DIRECT_CHANNEL_LABEL = 'Direct'

export const isDirectRow = (row) => !row?.channel || row.channel === 'none'

const SOURCE_NAMES = {
  ig: 'Instagram',
  instagram: 'Instagram',
  youtube: 'YouTube',
  yt: 'YouTube',
  tiktok: 'TikTok',
  facebook: 'Facebook',
  fb: 'Facebook',
  email: 'Email',
  'google-organic': 'Google',
}

export const OTHER_SOURCE_LABEL = 'Other'

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1)

export const getSourceName = (row) =>
  isDirectRow(row)
    ? DIRECT_CHANNEL_LABEL
    : SOURCE_NAMES[String(row.channel).toLowerCase()] || capitalize(String(row.channel))

// Direct first, then the most common named sources, then the rest summed as Other.
// Percents are whole numbers of all rows. Direct is always listed so a coach sees it at 0%.
export function getSourceBreakdown(rows = [], topCount = 3) {
  if (!rows.length) return []
  const counts = new Map()
  rows.forEach((row) => {
    const name = getSourceName(row)
    counts.set(name, (counts.get(name) || 0) + 1)
  })
  const directCount = counts.get(DIRECT_CHANNEL_LABEL) || 0
  counts.delete(DIRECT_CHANNEL_LABEL)
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1])
  const otherCount = ranked.slice(topCount).reduce((sum, [, count]) => sum + count, 0)
  const slices = [[DIRECT_CHANNEL_LABEL, directCount], ...ranked.slice(0, topCount)]
  if (otherCount) slices.push([OTHER_SOURCE_LABEL, otherCount])
  return slices.map(([name, count]) => ({ name, count, percent: Math.round((count / rows.length) * 100) }))
}

export function buildPurchaseReportCsv(rows = []) {
  const columns = getPurchaseReportColumns(rows)
  const lines = [columns.map(escapeCsvField).join(',')]
  rows.forEach((row) => {
    lines.push(columns.map((column) => escapeCsvField(cellValue(row || {}, column))).join(','))
  })
  return `${lines.join('\r\n')}\r\n`
}

// The callable's response is expected to be { rows: [...] }; a bare array is accepted too.
export function getReportRows(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.rows)) return data.rows
  return []
}

export function toDateInputValue(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

// Purchase rows start on this date, so the report never offers earlier days
export const REPORT_DATA_START = new Date(2026, 7, 15)

export function fromDateInputValue(value) {
  if (!value) return null
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export const DEFAULT_RANGE_DAYS = 7

export function getDefaultDateRange(now = new Date()) {
  const firstDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (DEFAULT_RANGE_DAYS - 1))
  const start = firstDay < REPORT_DATA_START ? REPORT_DATA_START : firstDay
  return { from: toDateInputValue(start), to: toDateInputValue(now) }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatDay = (date, withYear) =>
  `${MONTHS[date.getMonth()]} ${date.getDate()}${withYear ? `, ${date.getFullYear()}` : ''}`

// The summary's heading for a picked range: "Last 7 days" when it ends today, otherwise the dates.
export function getRangeLabel({ from, to }, now = new Date()) {
  const start = fromDateInputValue(from)
  const end = fromDateInputValue(to)
  if (!start || !end) return ''
  const days = Math.round((end - start) / 86400000) + 1
  if (to === toDateInputValue(now)) return days === 1 ? 'Today' : `Last ${days} days`
  if (from === to) return formatDay(start, start.getFullYear() !== now.getFullYear())
  const withYear = start.getFullYear() !== end.getFullYear() || end.getFullYear() !== now.getFullYear()
  return `${formatDay(start, withYear)} to ${formatDay(end, withYear)}`
}

// Turns the two date inputs into the [from, to] millisecond range the callable
// takes: the start of the first day through the end of the last day, local time.
export function dateInputsToRange(fromValue, toValue) {
  const [fy, fm, fd] = fromValue.split('-').map(Number)
  const [ty, tm, td] = toValue.split('-').map(Number)
  const from = new Date(fy, fm - 1, fd).getTime()
  const to = new Date(ty, tm - 1, td + 1).getTime() - 1
  return { from, to }
}

const ONE_YEAR_MS = 366 * 24 * 60 * 60 * 1000

export function validateDateInputs(fromValue, toValue) {
  if (!fromValue || !toValue) return 'Pick a start and end date.'
  const { from, to } = dateInputsToRange(fromValue, toValue)
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 'Pick a start and end date.'
  if (from > to) return 'The start date has to be on or before the end date.'
  if (from < REPORT_DATA_START.getTime()) return 'Data starts Aug 15, 2026. Pick a later start date.'
  if (to - from > ONE_YEAR_MS) return 'Pick a range of one year or less.'
  return null
}
